/* Les deux règles que partagent les graphiques de cet écran.
 *
 * Elles vivent ici parce qu'elles ont été fausses TROIS fois : chaque courbe
 * avait recopié la même géométrie, et le même défaut avec.
 */

/* La colonne de survol d'un point.
 *
 * ── Le défaut qu'elle corrige ───────────────────────────────────────────────
 *
 * Chaque point était coiffé d'une bande centrée sur lui, large d'un pas. Au
 * PREMIER point, cette bande commence à moins un demi-pas : la moitié tombe
 * hors du cadre et disparaît. Le dernier point perd la sienne de la même façon.
 * Les deux extrémités — c'est-à-dire la sortie la plus ancienne et celle
 * d'aujourd'hui, la plus regardée des deux — n'avaient donc qu'une demi-cible,
 * et un point posé au ras du bord devenait quasiment impossible à toucher au
 * doigt.
 *
 * Les bornes se prennent donc À MI-CHEMIN des voisins, et les deux du bout
 * vont jusqu'au bord du cadre. Aucune zone morte, aucun recouvrement, et le
 * premier comme le dernier point ont une cible pleine.
 */
export function colonne(
    i: number,
    total: number,
    x: (k: number) => number,
    largeurCadre: number,
): { x: number; width: number } {
    const gauche = i === 0 ? 0 : (x(i - 1) + x(i)) / 2;
    const droite = i === total - 1 ? largeurCadre : (x(i) + x(i + 1)) / 2;
    return { x: gauche, width: Math.max(1, droite - gauche) };
}

/* Où accrocher l'étiquette de survol.
 *
 * Centrée sur le point, elle déborde du cadre aux extrémités : la moitié du
 * texte sort à gauche du premier point, et le dernier va se poser sur les
 * graduations de l'axe. On bascule donc l'ancrage près des bords — le texte
 * part alors vers l'intérieur, où il y a la place.
 */
export function ancrage(px: number, largeur: number): 'start' | 'middle' | 'end' {
    if (px < largeur * 0.2) return 'start';
    if (px > largeur * 0.8) return 'end';
    return 'middle';
}

/* Le rayon d'une marque, selon le nombre de balles derrière elle.
 *
 * ── Pourquoi la racine, et pas le nombre ────────────────────────────────────
 *
 * C'est la SURFACE du disque qu'on lit, pas son rayon. Donner le rayon au
 * prorata du compte ferait d'un point à quarante balles une pastille seize fois
 * plus grosse que celle à dix : l'œil lirait quatre fois l'écart réel, et la
 * journée la plus fournie écraserait toutes les autres.
 *
 * ── Ce que la taille dit, et ce qu'elle ne dit pas ──────────────────────────
 *
 * Un plancher garde visible la marque d'une seule balle, ce qui casse la
 * proportionnalité stricte en bas de l'échelle. La taille se lit donc comme un
 * ORDRE DE GRANDEUR — grosse marque, beaucoup de balles, on peut s'y fier —
 * et jamais comme une mesure. Le compte exact reste au survol.
 */
/* Les deux bornes, en unités du cadre — pas en pixels.
 *
 * Le cadre fait 560 unités de large et se rend sur environ 340 pixels : une
 * unité vaut donc six dixièmes de pixel. À un rayon de 3, la marque la plus
 * maigre faisait trois pixels et demi de diamètre — invisible au soleil, sur un
 * écran tenu à bout de bras. Le plancher est celui de l'ancienne marque fixe,
 * pour que rien ne devienne plus petit qu'avant. */
const R_MIN = 4;
const R_MAX = 9;

export function rayon(balles: number, mini: number, maxi: number): number {
    if (!(maxi > mini)) return (R_MIN + R_MAX) / 2;
    const t = (Math.sqrt(Math.max(0, balles)) - Math.sqrt(mini))
        / (Math.sqrt(maxi) - Math.sqrt(mini));
    return R_MIN + Math.max(0, Math.min(1, t)) * (R_MAX - R_MIN);
}
